import { useState, useEffect, useCallback, useMemo, useRef, FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import 'dayjs/locale/es'
import { MainLayout } from 'views/layouts'
import {
  Container,
  ContainerActions,
  EditContainer,
  HorizontalDivider,
  SectionOne,
  SectionOneItem,
  SectionOneItemDescription,
  SectionOneItemNumber,
  TextAreaDiv,
  SearchButton,
  MagnifyingGlass,
  ButtonsContainer,
  ButtonSave,
  ButtonCancel,
  ButtonEdit,
  Test360Icon,
  DateWrapper,
  TitleIcon,
  HeaderContainer,
} from './styled'
import { H2, H5, P } from 'views/components/UI/Text'
import { StrokedButton, BackButton, Button } from 'views/components/UI/Buttons'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import Input from 'views/components/UI/TextInput'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import IconMagnifying from 'assets/icons/iconMagnifying.png'
import BluesiteTableComponent from 'views/components/UI/BluesiteTable'
import {
  HEADERS_360_USERS_EDITABLE,
  HEADERS_ADDED_USERS_EDITABLE,
  HEADERS_EVALUATORS_EDITABLE,
  UserAdded,
} from '../Create/Users/constants'
import { useNotification } from 'lib/context/notification.context'
import { platformAdminRoutes } from 'router/routes'
import { Row } from 'views/components/UI/Row'
import {
  stepOneSelector,
  stepThreeSelector,
  stepFiveSelector,
  setUserListAdded,
  setStepFiveEmailTemplate,
  getEmailTemplateStepFive,
} from 'ducks/createProcess'
import { companyIdSelector } from 'ducks/user'
import { useGetInventoryById } from 'hooks/inventory/useInventory'
import useDeleteUsersInProcess from 'hooks/process/useDeleteUsersInProcess/use-delete-users-in-process'
import useGetCompanyDetail from 'hooks/company/useGetCompanyDetail/useGetCompanyDetail'
import { useSearchUsers } from 'hooks/users/useSearchUser/useSearchUser'
import useCreateUsersBatch from 'hooks/users/useCreateUsersBatch/useCreaterUsersBatch'
import useGetUsersByProcess from 'hooks/process/useGetUsersByProcess/useGetUsersByProcess'
import { useSendExamToUsers } from 'hooks/process/useSendExamToUsers/useSendExamToUsers'
import { DEFAULT_LIMIT } from 'constants/global.constants'
import { getCountQuestionnaires, deleteUsersDuplicate } from './main'
import _ from 'lodash'
import { mergeUsers } from '../Create/Users/main'
import { hasAnsweredTest } from '../Create/Users/test-status'
import { getLogoUrl } from 'views/screens/Platform-administrator/constants/fetch_process'
import { useTranslation } from 'react-i18next'
import updateProcessUser from 'services/common/users/update-process-user'
import { MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material'
import { updateLanguage } from '../../services'
import { updateProcess } from 'services/common/process/createProcess/create-process-service'
import {
  selectEvaluatedUsers,
  selectEvaluators,
  set360Data,
  setEvaluatedUsers,
  setEvaluators,
  setShowSummary as setReduxShowSummary,
  setShowIntro as setReduxShowIntro,
  setHas360Test,
  selectFinalDate,
  setFinalDate,
} from 'ducks/test360'
import EditIcon from 'assets/icons/EditCoin'
import DeleteIcon from 'assets/icons/DeleteIcon'
import threeSixtyIcon from 'assets/images/360-clean-icon.png'
import TooltipComponent from 'views/components/Tooltip'
import { usePreventNavigation } from 'views/screens/super-admin/homologation-skills/hooks/usePreventNavigation'
import test360UpdateDraft from 'services/process/test360/test360UpdateDraft'
import updateProcessFinalDate from 'services/process/updateProcessFinalDate'
import test360DeleteUsers from 'services/process/test360/test360DeleteUsers'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import test360SaveFinalData from 'services/process/test360/test360SaveFinalData'

interface SearchParams {
  name?: string
  email?: string
  identification?: string
}

interface UserLanguages {
  [userId: number]: string
}

function UsersCreateProcess() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  dayjs.extend(utc)
  dayjs.extend(customParseFormat)
  dayjs.locale('es')
  const users360TableRef = useRef<HTMLDivElement>(null)
  const { getSuccess, getWarning, getError } = useNotification()
  const { getOneCompany, companyInfo } = useGetCompanyDetail()
  const { loading, createUsersBatch, resultsSucess, resultsFail } = useCreateUsersBatch()
  const { deleteUsersInProcess } = useDeleteUsersInProcess()

  const { search, results, clearSearch } = useSearchUsers()
  const { handleSendExamToUsers, loadingSendExamToUser, resultSendExamToUser, setLoading } = useSendExamToUsers()

  const companyId = useSelector(companyIdSelector)
  const stepOneData = useSelector(stepOneSelector)
  const stepFiveData = useSelector(stepFiveSelector)
  const users360Data = useSelector(selectEvaluatedUsers)
  const evaluators360Data = useSelector(selectEvaluators)
  const newUserId = location?.state?.newUserId || null

  const { isUsersLoaded, addedUsers: usersDB } = useGetUsersByProcess(stepOneData)
  const [newUserIdCreated, setNewUserIdCreated] = useState<number | null>(newUserId)

  const templateEmailStepFive = useSelector(getEmailTemplateStepFive)
  const [refreshInventory, setRefreshInventory] = useState(false)
  const { inventoryData } = useGetInventoryById(stepOneData.inventoryTypeId, refreshInventory)
  const { inventoryUniqName, processId } = stepOneData
  const stepThreeData = useSelector(stepThreeSelector)
  const testDate = useSelector(selectFinalDate)
  const usersAddedOnPrevStep = location.state?.data ? deleteUsersDuplicate(location.state?.data) : []
  const amountInventory = inventoryData ? inventoryData.credits - inventoryData.usedCredits : 0

  const [searchOnEnter, setSearchOnEnter] = useState(false)

  const [executedCreateUser, setExecuteCreateUser] = useState(false)
  const [pageUsersAdded, setPageUsersAdded] = useState(1)
  const [pageResults, setPageResults] = useState(1)

  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    if (resultSendExamToUser.status === 'error') {
      setShowModal(false)
    }
  }, [resultSendExamToUser.status])

  const [name, setName] = useState('')
  const [identification, setIdentification] = useState('')
  const [email, setEmail] = useState('')

  const uploadType = location.state?.uploadedbyFile
  const tryFlag = location.state?.tryAgain
  const [addedUsers, setAddedUsers] = useState<UserAdded[]>([])
  const [usersFailed] = useState<UserAdded[]>([])
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set())
  const [searchResults, setSearchResults] = useState(addedUsers)
  const [totalPages, setTotalPages] = useState(0)
  const [tableLoading, setTableLoading] = useState(false)
  const [userLanguages, setUserLanguages] = useState<UserLanguages>({})
  const [pageEvaluators, setPageEvaluators] = useState(1)
  const [test360Date, setTest360Date] = useState<string>()
  const [evaluators360, setEvaluators360] = useState<UserAdded[]>([])
  const [hasInitialized, setHasInitialized] = useState(false)

  const testTypesString = useMemo(() => {
    return `${stepThreeData?.useBetesaTest ? (stepThreeData.useSkillsTest ? 'Betesa - ' : 'Betesa ') : ''} ${
      stepThreeData?.useSkillsTest ? 'Competencias' : ''
    }`
  }, [stepThreeData?.useBetesaTest, stepThreeData?.useSkillsTest])

  useEffect(() => {
    if (testDate) {
      setTest360Date(testDate)
    }
  }, [testDate])

  const initialize360Users = useCallback(() => {
    if (users360Data.length > 0 && !hasInitialized) {
      const formattedUsers = users360Data.map((user: any) => ({
        ...user,
        id: user.user_id,
        document: user.document,
        test_types: testTypesString.length > 1 ? `${testTypesString} - 360°` : '360°',
      }))

      setAddedUsers(formattedUsers)
      setHasInitialized(true)
    }
  }, [users360Data, hasInitialized, testTypesString])

  useEffect(() => {
    initialize360Users()
  }, [initialize360Users])

  useEffect(() => {
    if (evaluators360Data.length > 0) {
      const formattedEvaluators = evaluators360Data.map((evaluator: any) => ({
        ...evaluator,
        id: evaluator.user_id,
        document: evaluator.document,
      }))

      setEvaluators360(formattedEvaluators)
    }
  }, [evaluators360Data])

  const paginatedAddedUsers = useMemo(() => {
    if (addedUsers.length === 0) return []
    setTotalPages(Math.ceil(searchResults.length / 10))
    const firstIndex = (pageUsersAdded - 1) * DEFAULT_LIMIT
    const lastIndex = firstIndex + DEFAULT_LIMIT
    const userMerged = searchResults.slice(firstIndex, lastIndex)
    const usersFormatted = userMerged.map((user) => ({
      ...user,
      test_types:
        user.test_types && user.test_types.includes('Competencias')
          ? user.test_types.replace('Competencias', t('global.common.skillText'))
          : user.test_types,
    }))

    if (users360Data.length > 0) {
      const uniqueUsers: Record<string, boolean> = {}
      return usersFormatted.filter((user) => {
        if (user.email && uniqueUsers[user.email]) {
          return false
        }

        if (user.email) {
          uniqueUsers[user.email] = true
        }
        return true
      })
    } else {
      return _.uniqBy(usersFormatted, 'document')
    }
  }, [addedUsers, pageUsersAdded, searchResults, t, users360Data.length])

  const searchUsers = (searchParams: SearchParams) => {
    setPageUsersAdded(1)
    return addedUsers.filter((user) => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase()
      const searchName = (searchParams.name || '').toLowerCase().replace(/\s/g, '')

      const userEmail = (user.email || '').toLowerCase().replace(/\s/g, '')
      const searchMail = (searchParams.email || '').toLowerCase().replace(/\s/g, '')

      const userIdentification = (user.document || '').toLowerCase().replace(/\s/g, '')
      const searchIdentification = (searchParams.identification || '').toLowerCase().replace(/\s/g, '')

      const typesOfTest = (user.test_types || '').toLowerCase().replace(/\s/g, '')

      return (
        (fullName.includes(searchName) || typesOfTest.includes(searchName)) &&
        userEmail.includes(searchMail) &&
        userIdentification.includes(searchIdentification)
      )
    })
  }

  useEffect(() => {
    if (name || email || identification) {
      const resultsOfSearch = searchUsers({ name, email, identification })
      setSearchResults(resultsOfSearch)
    } else {
      setSearchResults(addedUsers)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, email, identification, addedUsers])

  const handleCreateUsers = useCallback(async () => {
    if (executedCreateUser) return

    setExecuteCreateUser(true)

    const listUsersWithoutAccount = usersAddedOnPrevStep
      .filter((user: UserAdded) => typeof user.id !== 'number')
      .map((user: UserAdded) => {
        return {
          ...user,
          firstName: user.first_name,
          lastName: user.last_name,
        }
      })

    const usersPrevCreated = usersAddedOnPrevStep
      .filter((user: UserAdded) => typeof user.id === 'number')
      .map((user: UserAdded) => {
        return {
          ...user,
          firstName: user.first_name,
          lastName: user.last_name,
        }
      })

    if (usersPrevCreated.length) {
      setAddedUsers([...usersPrevCreated] as any)
    } else {
      usersPrevCreated.map((user: any) => {
        return {
          ...user,
          firstName: user.first_name,
          lastName: user.last_name,
        }
      })
      if (stepFiveData.userListAdded && newUserIdCreated) {
        const usersFormated = stepFiveData.userListAdded?.map((user: any) => {
          return {
            ...user,
            firstName: user.first_name,
            lastName: user.last_name,
          }
        })

        const mergedUsers = mergeUsers(usersFormated, usersPrevCreated, newUserId)

        const uniqUsers = _.uniqBy(mergedUsers, 'document')
        setAddedUsers([...uniqUsers] as any)
        setNewUserIdCreated(null)
      } else if (usersPrevCreated.length > 0) {
        setAddedUsers([...usersPrevCreated] as any)
      }
    }

    if (
      executedCreateUser === false &&
      listUsersWithoutAccount.length > 0 &&
      stepFiveData.userListFailed?.length === 0
    ) {
      setExecuteCreateUser(true)
      createUsersBatch(listUsersWithoutAccount as any)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepFiveData.userListAdded])

  const hasRun = useRef(false)

  useEffect(() => {
    getOneCompany(companyId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  useEffect(() => {
    if (companyInfo && !templateEmailStepFive) {
      const newDateTemplate = dayjs.utc().add(7, 'day').format('DD/MM/YYYY')
      dispatch(
        setStepFiveEmailTemplate({
          textOne: `Como parte del proceso que estás realizando para ${companyInfo?.data.name}, te invitamos a leer detenidamente la información contenida en este correo.`,
          textTwo: searchResults.some((item) => item.active === true)
            ? `Tu cuenta en Bluesite está activa. Ingresa con tu correo y la clave que creaste cuando ingresaste por
          primera vez. Si no la recuerdas, puedes hacer click en "Olvidé mi Contraseña" para restaurarla`
            : `Para ingresar a la plataforma Bluesite y diligenciar los cuestionarios asignados, que nos ayudarán a conocer mejor tu perfil, completa los siguientes pasos:`,
          textThree: `Por favor, ten en cuenta lo siguiente:`,
          textFour: `Ten presente que, una vez finalices el cuestionario, tus resultados serán compartidos con el equipo de ${companyInfo?.data.name}, quienes los revisarán y te contactarán para indicar los siguientes pasos en tu proceso.`,
          textThreeOptions: searchResults.some((item) => item.active === true)
            ? [
                `La fecha límite para que llenes los cuestionarios asignados es el ${newDateTemplate} `,
                'Debes disponer de al menos 1 hora y 30 minutos para realizar el proceso.',
                'Elige un lugar tranquilo que te brinde privacidad, silencio y buena iluminación.',
              ]
            : [
                'Debes disponer de alrededor de 40 minutos por prueba asignada.',
                'Elige un lugar tranquilo que te brinde privacidad, silencio y buena iluminación.',
              ],
          date: dayjs.utc().add(7, 'day').format('DD/MM/YYYY'),
          active: searchResults.some((item) => item.active === true),
        })
      )
    }
  }, [companyInfo, dispatch, searchResults, templateEmailStepFive])
  // start
  useEffect(() => {
    if (!hasRun.current && !loading && !isUsersLoaded) {
      handleCreateUsers()
      hasRun.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isUsersLoaded, usersDB])

  useEffect(() => {
    if (searchOnEnter) {
      search({ documentFilter: identification, nameFilter: name, emailFilter: email, processId })
      setSearchOnEnter(false)
    }
  }, [search, searchOnEnter, identification, name, email, processId])

  useEffect(() => {
    if (hasRun.current && isUsersLoaded && addedUsers.length === 0 && users360Data.length === 0 && !hasInitialized) {
      setAddedUsers(usersDB as any)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUsersLoaded, usersDB, hasInitialized])

  useEffect(() => {
    if (!isUsersLoaded || usersDB.length === 0) return
    const statusesByDocument = new Map<string, UserAdded['test_statuses']>(
      usersDB.map((user) => [user.document, user.test_statuses])
    )
    setAddedUsers((currentUsers) => currentUsers.map((user) => {
      const testStatuses = statusesByDocument.get(user.document)
      return testStatuses ? { ...user, test_statuses: testStatuses } : user
    }))
  }, [isUsersLoaded, usersDB, addedUsers.length])

  const handlePagination = useCallback((page: number) => {
    setPageUsersAdded(page)

    setTimeout(() => {
      if (users360TableRef.current) {
        users360TableRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }, 100)
  }, [])

  const handlePaginationResults = useCallback(
    (page: number) => {
      setPageResults(page)
      search({ documentFilter: identification, nameFilter: name, emailFilter: email, page: page.toString(), processId })
    },
    [email, identification, name, search, processId]
  )

  const handleRemoveUser360 = async (userEmail: string) => {
    const userToDelete = addedUsers.find((user: any) => user.email === userEmail)

    if (!userToDelete) {
      getWarning('No se encontró el usuario seleccionado')
      return
    }

    try {
      // Call the new service to delete the user
      const deleteResponse = await test360DeleteUsers(stepOneData.processId, [userEmail])

      // Handle error response
      if (!deleteResponse.success) {
        getError(deleteResponse.message || 'Ha ocurrido un error al intentar eliminar el usuario')
        return
      }

      // Update local state after successful deletion
      const evaluatorsToKeep = new Set<string>()

      const updatedUsers360 = addedUsers.filter((user: any) => {
        if (user.email !== userEmail && user.evaluators) {
          user.evaluators.forEach((evaluator: any) => {
            evaluatorsToKeep.add(evaluator.email)
          })
          return true
        }
        return false
      })

      const updatedEvaluators = evaluators360Data.filter((evaluator: any) => evaluatorsToKeep.has(evaluator.email))

      const removedEvaluatorsCount = evaluators360Data.length - updatedEvaluators.length

      setAddedUsers(updatedUsers360)
      setSelectedItems(new Set())
      dispatch(setUserListAdded([...updatedUsers360]))
      dispatch(setEvaluatedUsers(updatedUsers360))
      dispatch(setEvaluators(updatedEvaluators))

      if (removedEvaluatorsCount > 0) {
        getSuccess(
          `Usuario eliminado correctamente de la base de datos. También se eliminaron ${removedEvaluatorsCount} evaluadores que solo estaban relacionados con este usuario.`
        )
      } else {
        getSuccess(
          'Usuario eliminado correctamente de la base de datos. No se eliminaron evaluadores ya que todos están relacionados con otros usuarios.'
        )
      }

      if (updatedUsers360.length === 0) {
        getWarning('No hay usuarios agregados, por favor agrega usuarios para continuar')
        setTimeout(() => {
          navigate(platformAdminRoutes.CreateUsers)
        }, 2000)
      }

      setTimeout(() => {
        setRefreshInventory(!refreshInventory)
      }, 500)
    } catch (error) {
      console.error('Error al eliminar el usuario 360:', error)
      getError('Ocurrió un error al eliminar el usuario. Por favor, inténtalo de nuevo.')
    }
  }

  const handleRemoveUser = async (userDocument: string): Promise<void> => {
    let warningMessage = ''
    if (selectedItems.size > 0) {
      const idUsersToDelete = addedUsers
        .filter((user: any) => selectedItems.has(user.userDocument))
        .map((user: any) => user.id)
      if (idUsersToDelete.length > 0 && !(await deleteUsersInProcess(stepOneData.processId, idUsersToDelete))) return

      const updatedUsers = addedUsers.filter((user: any) => user.document !== userDocument)
      setAddedUsers(updatedUsers)
      setSelectedItems(new Set())
      dispatch(setUserListAdded([...updatedUsers]))

      if (updatedUsers.length === 0) {
        getWarning('No hay usuarios agregados, por favor agrega usuarios para continuar')
        setTimeout(() => {
          navigate(platformAdminRoutes.CreateUsers)
        }, 2000)
      }
      warningMessage = 'Se eliminaron los usuarios seleccionados'
    } else {
      const updatedUsers = addedUsers.filter((user: any) => user.document !== userDocument)
      const idUserToDelete = addedUsers.find((user: any) => user.document === userDocument)?.id

      if (typeof idUserToDelete === 'number' && !(await deleteUsersInProcess(stepOneData.processId, [idUserToDelete]))) return

      if (updatedUsers.length % 10 === 0 && pageUsersAdded > 1) {
        setPageUsersAdded((prev) => prev - 1)
      }

      setAddedUsers(updatedUsers)
      dispatch(setUserListAdded([...updatedUsers]))

      if (updatedUsers.length === 0) {
        getWarning('No hay usuarios agregados, por favor agrega usuarios para continuar')
        setTimeout(() => {
          navigate(platformAdminRoutes.CreateUsers)
        }, 2000)
      }
      warningMessage = 'Se eliminó el usuario seleccionado'
    }
    getWarning(warningMessage)
    setTimeout(() => {
      setRefreshInventory(!refreshInventory)
    }, 500)
  }

  // MARK: 360 CLEANUP

  const handleCleanup = () => {
    // Reset Redux state
    dispatch(set360Data([]))
    dispatch(setReduxShowSummary(false))
    dispatch(setReduxShowIntro(true))
    dispatch(setEvaluatedUsers([]))
    dispatch(setEvaluators([]))
    dispatch(setHas360Test(false))
    dispatch(setFinalDate(''))
  }

  const handleSendToUsers = async () => {
    setShowModal(true)
    setLoading(true)
    localStorage.removeItem('modelSkills')
    localStorage.removeItem('aiSkills')

    // Usar directamente addedUsers ya que está actualizado cuando el usuario hace click
    let usersToSend = [...addedUsers]
    let is360 = false

    if (evaluators360Data.length > 0) {
      usersToSend = [...usersToSend, ...evaluators360]
      is360 = true

      if (test360Date) {
        test360SaveFinalData(processId, test360Date)
          .then((response) => {
            if (response?.message !== 'Success') {
              getWarning('Error al guardar la fecha de cierre del test 360°')
            }
          })
          .catch((error) => {
            console.error('Error saving test 360 final date:', error)
            getWarning('Error al guardar la fecha de cierre del test 360°')
          })
      }
    }

    const changedMailUsersActive = _.uniqBy(
      usersToSend.filter((user) => typeof user.id === 'number' && user.active === true && user.emailEdit === true),
      'id'
    ).map((user) => user.id) as number[]

    const changedMailUsersInactive = _.uniqBy(
      usersToSend.filter(
        (user) => typeof user.id === 'number' && (user.active === false || !user.active) && user.emailEdit === true
      ),
      'id'
    ).map((user) => user.id) as number[]

    const activeUsersIds = _.uniqBy(
      usersToSend.filter((user) => typeof user.id === 'number' && user.active === true && !user.emailEdit),
      'id'
    ).map((user) => user.id) as number[]

    const inactiveUsersIds = _.uniqBy(
      usersToSend.filter(
        (user) => typeof user.id === 'number' && (user.active === false || !user.active) && !user.emailEdit
      ),
      'id'
    ).map((user) => user.id) as number[]

    // Debug: verificar categorización de usuarios
    console.log('📊 Categorización de usuarios:', {
      usersToSend: usersToSend.map((u) => ({ id: u.id, email: u.email, active: u.active, emailEdit: u.emailEdit })),
      activeUsersIds,
      inactiveUsersIds,
      changedMailUsersActive,
      changedMailUsersInactive,
      totalCalls:
        activeUsersIds.length +
        inactiveUsersIds.length +
        changedMailUsersActive.length +
        changedMailUsersInactive.length,
    })

    let logo: string
    try {
      await updateProcess(stepOneData.processId, { finishProcess: true })
      const logoData = await getLogoUrl(processId)
      if (!logoData?.cobranding?.logo) {
        throw new Error('Logo del proceso no disponible')
      }
      logo = logoData.cobranding.logo
    } catch (error) {
      console.error('Error preparing test delivery:', error)
      setShowModal(false)
      setLoading(false)
      getError('No se pudo preparar el envío de pruebas')
      return
    }

    const newDateTemplate = dayjs.utc().add(7, 'day').format('DD/MM/YYYY')
    const emailTemplateStepFiveOne = {
      textOne: templateEmailStepFive?.textOne,
      textTwo: `Tu cuenta en Bluesite está activa. Ingresa con tu correo y la clave que creaste cuando ingresaste por
      primera vez. Si no la recuerdas, puedes hacer click en "Olvidé mi Contraseña" para restaurarla`,
      textThree: templateEmailStepFive?.textThree,
      textFour: templateEmailStepFive?.textFour,
      textThreeOptions: [
        `La fecha límite para que llenes los cuestionarios asignados es el ${
          templateEmailStepFive?.date || newDateTemplate
        }`,
        'Debes disponer de al menos 1 hora y 30 minutos para realizar el proceso.',
        'Elige un lugar tranquilo que te brinde privacidad, silencio y buena iluminación.',
      ],
      date: templateEmailStepFive?.date || newDateTemplate,
      active: true,
      logo,
    }

    const emailTemplateStepFiveTwo = {
      textOne: templateEmailStepFive?.textOne,
      textTwo: `Para ingresar a la plataforma Bluesite y diligenciar los cuestionarios asignados, que nos ayudarán a conocer mejor tu perfil, completa los siguientes pasos:`,
      textThree: templateEmailStepFive?.textThree,
      textFour: templateEmailStepFive?.textFour,
      textThreeOptions: [
        'Debes disponer de alrededor de 40 minutos por prueba asignada.',
        'Elige un lugar tranquilo que te brinde privacidad, silencio y buena iluminación.',
      ],
      date: templateEmailStepFive?.date || newDateTemplate,
      active: false,
      logo,
    }

    let totalCalls = 0
    let completedCalls = 0

    if (changedMailUsersActive.length > 0) totalCalls++
    if (changedMailUsersInactive.length > 0) totalCalls++
    if (activeUsersIds.length > 0) totalCalls++
    if (inactiveUsersIds.length > 0) totalCalls++

    if (activeUsersIds.length > 0) {
      const usersData = activeUsersIds.map((userId) => {
        const user = usersToSend.find((item) => item.id === userId)
        const language = user && user.language
        return {
          user_id: userId,
          language: language || userLanguages[userId] || 'es',
        }
      })
      const dataToSend = {
        process_id: processId,
        users_data: usersData,
        company_id: companyId,
        type: 'test_send',
        email_template: emailTemplateStepFiveOne,
        is360,
      }

      setTimeout(() => {
        completedCalls++
        handleSendExamToUsers(dataToSend, totalCalls, completedCalls)
      }, 3500)
    }

    if (inactiveUsersIds.length > 0) {
      const usersData = inactiveUsersIds.map((userId) => {
        const user = usersToSend.find((item) => item.id === userId)
        const language = user && user.language
        return {
          user_id: userId,
          language: language || userLanguages[userId] || 'es',
        }
      })
      const dataToSendInactive = {
        process_id: processId,
        users_data: usersData,
        company_id: companyId,
        type: 'test_send',
        email_template: emailTemplateStepFiveTwo,
        is360,
      }

      setTimeout(() => {
        completedCalls++
        handleSendExamToUsers(dataToSendInactive, totalCalls, completedCalls)
      }, 4000)
    }

    if (changedMailUsersActive.length > 0) {
      const usersData = changedMailUsersActive.map((userId) => {
        const user = usersToSend.find((item) => item.id === userId)
        const language = user && user.language
        return {
          user_id: userId,
          language: language || userLanguages[userId] || 'es',
        }
      })
      const dataToSend = {
        process_id: processId,
        users_data: usersData,
        company_id: companyId,
        type: 'test_resend',
        count: 0,
        email_template: emailTemplateStepFiveOne,
        is360,
      }

      setTimeout(() => {
        completedCalls++
        handleSendExamToUsers(dataToSend, totalCalls, completedCalls)
      }, 4500)
    }

    if (changedMailUsersInactive.length > 0) {
      const usersData = changedMailUsersInactive.map((userId) => {
        const user = usersToSend.find((item) => item.id === userId)
        const language = user && user.language
        return {
          user_id: userId,
          language: language || userLanguages[userId] || 'es',
        }
      })
      const dataToSend = {
        process_id: processId,
        users_data: usersData,
        company_id: companyId,
        type: 'test_send_email_change',
        count: 0,
        email_template: emailTemplateStepFiveTwo,
        is360,
      }

      setTimeout(() => {
        completedCalls++
        handleSendExamToUsers(dataToSend, totalCalls, completedCalls)
      }, 5000)
    }

    // Si no hay usuarios para enviar, cerrar el modal inmediatamente
    if (totalCalls === 0) {
      setShowModal(false)
      setLoading(false)
      getWarning('No hay usuarios para enviar pruebas')
      return
    }

    // El modal se cerrará después de que se completen todos los envíos
    setTimeout(handleCleanup, 6000)
  }

  const ActionDeleteUser = (item: any) => {
    const { document, email: email360 } = item
    return (
      <ContainerActions>
        {!hasAnsweredTest(item.test_statuses, item.test_sent) && (
          <EditContainer
            onClick={() => {
              if (users360Data.length) {
                handleRemoveUser360(email360)
              } else {
                handleRemoveUser(document)
              }
            }}
          >
            <DeleteIcon />
          </EditContainer>
        )}
      </ContainerActions>
    )
  }

  const handleSave = async (rowIndex: number, editingRow: any) => {
    try {
      setTableLoading(true)

      const originalUser = addedUsers.find((user) => user.id === editingRow.id)
      let updatedEditingRow

      // Marcar emailEdit si el email cambió, independientemente de si ya se envió la prueba
      if (originalUser && originalUser.email !== editingRow.email) {
        updatedEditingRow = { ...editingRow, emailEdit: true }
      }

      const response = await updateProcessUser({
        userId: editingRow.id,
        firstName: editingRow.first_name,
        lastName: editingRow.last_name,
        email: editingRow.email.toLowerCase(),
        document: editingRow.document,
      })
      if (response.status === 'OK') {
        const { edit, ...rest } = updatedEditingRow ? updatedEditingRow : editingRow
        setAddedUsers((prev) => {
          const userIndex = prev.findIndex((user) => user.id === rest.id)
          if (userIndex !== -1) {
            const newUsers = [...prev]
            const currentLanguage = prev[userIndex].language || 'es'
            const updatedUser = { ...rest, language: currentLanguage, evaluated_role: prev[userIndex].evaluated_role }
            newUsers[userIndex] = updatedUser
            dispatch(setUserListAdded(newUsers))
            if (users360Data.length > 0) {
              dispatch(setEvaluatedUsers(newUsers))

              // Validar si el usuario también es evaluador
              const currentUpdatedUser = rest
              const updatedEvaluators360 = [...evaluators360]
              const bodyToSend: any = {
                test360: {
                  users: newUsers.map((user: any) => {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    const { id, user_id, user_test_id, document, test_types, evaluators, emailEdit, ...userRest } = user
                    return {
                      ...userRest,
                      evaluated_role: user.evaluated_role,
                      evaluators: evaluators?.map((evaluator: any) => {
                        const {
                          id: evalId,
                          user_id: evalUserId,
                          user_test_id: evalTestId,
                          document: evalDoc,
                          emailEdit: evalEmailEdit,
                          ...evaluatorRest
                        } = evaluator
                        return evaluatorRest
                      }),
                    }
                  }),
                },
              }

              // Buscar si el usuario también es evaluador por user_id
              const evaluatorIndex = updatedEvaluators360.findIndex(
                (evaluator) => evaluator.id === currentUpdatedUser.id
              )

              if (evaluatorIndex !== -1) {
                // El usuario también es evaluador, actualizar sus datos en evaluators360
                updatedEvaluators360[evaluatorIndex] = {
                  ...updatedEvaluators360[evaluatorIndex],
                  first_name: currentUpdatedUser.first_name,
                  last_name: currentUpdatedUser.last_name,
                  email: currentUpdatedUser.email,
                  document: currentUpdatedUser.document,
                }

                // Actualizar también el array evaluators dentro de cada usuario
                const updatedUsersWithEvaluators = newUsers.map((user: any) => {
                  if (user.evaluators && Array.isArray(user.evaluators)) {
                    const updatedEvaluatorsInUser = user.evaluators.map((evaluator: any) => {
                      if (evaluator.id === currentUpdatedUser.id || evaluator.user_id === currentUpdatedUser.id) {
                        return {
                          ...evaluator,
                          first_name: currentUpdatedUser.first_name,
                          last_name: currentUpdatedUser.last_name,
                          email: currentUpdatedUser.email,
                          document: currentUpdatedUser.document,
                        }
                      }
                      return evaluator
                    })
                    return { ...user, evaluators: updatedEvaluatorsInUser }
                  }
                  return user
                })

                // Actualizar el body con los usuarios que tienen evaluators actualizados
                bodyToSend.test360.users = updatedUsersWithEvaluators.map((user: any) => {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  const { id, user_id, user_test_id, test_types, document, evaluators, emailEdit, ...userRest } = user
                  return {
                    ...userRest,
                    evaluated_role: user.evaluated_role,
                    evaluators: evaluators?.map((evaluator: any) => {
                      const {
                        id: evalId,
                        user_id: evalUserId,
                        user_test_id: evalTestId,
                        document: evalDoc,
                        emailEdit: evalEmailEdit,
                        ...evaluatorRest
                      } = evaluator
                      return evaluatorRest
                    }),
                  }
                })

                // Agregar evaluators al body para enviar al servicio
                bodyToSend.test360.evaluators = updatedEvaluators360.map((evaluator: any) => {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  const { id, user_id, user_test_id, document, evaluators, emailEdit, ...evaluatorRest } = evaluator
                  return evaluatorRest
                })

                // Actualizar el estado de evaluators360
                setEvaluators360(updatedEvaluators360)
                dispatch(setEvaluators(updatedEvaluators360))

                // Actualizar también el estado de usuarios con los evaluators modificados
                dispatch(setEvaluatedUsers(updatedUsersWithEvaluators))
              }

              test360UpdateDraft(stepOneData.processId, bodyToSend)
                .then((updateResponse) => {
                  // Manejar respuesta del endpoint
                  if (!updateResponse.success) {
                    getError(updateResponse.message)
                  }
                })
                .catch((error) => {
                  console.error('Error updating test360 draft:', error)
                  getError('Error al actualizar el borrador del Test 360')
                })
            }
            return newUsers
          }
          return prev
        })
        getSuccess(t('global.notifications.updateUserSuccess'))
      }
      if (response.status === 'error' && response.message === 'Email or document already exists') {
        getWarning(t('global.notifications.updateUserError'))
      }
      setTableLoading(false)
    } catch (error) {
      setTableLoading(false)
      getError(t('global.notifications.updateUserErrorUnexpected'))
      console.error('error updating user', error)
    }
  }

  const handleSaveEvaluator = async (rowIndex: number, editingRow: any) => {
    try {
      setTableLoading(true)

      const originalEvaluator = evaluators360.find((evaluator) => evaluator.id === editingRow.id)
      let updatedEditingRow

      // Marcar emailEdit si el email cambió, independientemente de si ya se envió la prueba
      if (originalEvaluator && originalEvaluator.email !== editingRow.email) {
        updatedEditingRow = { ...editingRow, emailEdit: true }
      }

      const response = await updateProcessUser({
        userId: editingRow.id,
        firstName: editingRow.first_name,
        lastName: editingRow.last_name,
        email: editingRow.email.toLowerCase(),
        document: editingRow.document,
      })

      if (response.status === 'OK') {
        const { edit, ...rest } = updatedEditingRow ? updatedEditingRow : editingRow

        setEvaluators360((prev) => {
          const evaluatorIndex = prev.findIndex((evaluator) => evaluator.id === rest.id)
          if (evaluatorIndex !== -1) {
            const newEvaluators = [...prev]
            const currentLanguage = prev[evaluatorIndex].language
            const updatedEvaluator = { ...rest, language: currentLanguage }
            newEvaluators[evaluatorIndex] = updatedEvaluator

            dispatch(setEvaluators(newEvaluators))

            return newEvaluators
          }
          return prev
        })

        setAddedUsers((prevUsers) => {
          return prevUsers.map((user) => {
            if ((user as any).evaluators && Array.isArray((user as any).evaluators)) {
              const updatedEvaluators = (user as any).evaluators.map((evaluator: any) => {
                if (evaluator.user_id === rest.id || String(evaluator.user_id) === String(rest.id)) {
                  return {
                    ...evaluator,
                    first_name: rest.first_name,
                    last_name: rest.last_name,
                    email: rest.email.toLowerCase(),
                    document: rest.document,
                    emailEdit: rest.emailEdit,
                  }
                }
                return evaluator
              })
              return { ...user, evaluators: updatedEvaluators }
            }
            return user
          })
        })

        // Actualizar los evaluadores dentro de cada usuario evaluado
        const updatedUsers360Data = addedUsers.map((user: any) => {
          if ((user as any).evaluators && Array.isArray((user as any).evaluators)) {
            const updatedEvaluators = (user as any).evaluators.map((evaluator: any) => {
              if (evaluator.user_id === rest.id || String(evaluator.user_id) === String(rest.id)) {
                return {
                  ...evaluator,
                  first_name: rest.first_name,
                  last_name: rest.last_name,
                  email: rest.email.toLowerCase(),
                  document: rest.document,
                  emailEdit: rest.emailEdit,
                }
              }
              return evaluator
            })
            return { ...user, evaluators: updatedEvaluators }
          }
          return user
        })

        // Validar si el evaluador también es usuario
        const currentUpdatedEvaluator = rest
        const evaluatorIsAlsoUser = updatedUsers360Data.findIndex((user) => user.id === currentUpdatedEvaluator.id)

        const finalUpdatedUsers = [...updatedUsers360Data]

        if (evaluatorIsAlsoUser !== -1) {
          // El evaluador también es usuario, actualizar sus datos en el array de usuarios
          finalUpdatedUsers[evaluatorIsAlsoUser] = {
            ...finalUpdatedUsers[evaluatorIsAlsoUser],
            first_name: currentUpdatedEvaluator.first_name,
            last_name: currentUpdatedEvaluator.last_name,
            email: currentUpdatedEvaluator.email,
            document: currentUpdatedEvaluator.document,
          }
        }

        // Actualizar el estado de usuarios
        setAddedUsers(finalUpdatedUsers)
        dispatch(setUserListAdded(finalUpdatedUsers))
        dispatch(setEvaluatedUsers(finalUpdatedUsers))

        // Preparar body para enviar al servicio con usuarios y evaluadores actualizados
        const bodyToSend: any = {
          test360: {
            users: finalUpdatedUsers.map((user: any) => {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              const { id, user_id, user_test_id, test_types, document, evaluators, emailEdit, ...userRest } = user
              return {
                ...userRest,
                evaluated_role: user.evaluated_role,
                evaluators: evaluators?.map((evaluator: any) => {
                  const {
                    id: evalId,
                    user_id: evalUserId,
                    user_test_id: evalTestId,
                    document: evalDoc,
                    emailEdit: evalEmailEdit,
                    ...evaluatorRest
                  } = evaluator
                  return evaluatorRest
                }),
              }
            }),
            evaluators: evaluators360.map((evaluator: any) => {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              const { id, user_id, user_test_id, document, evaluators, emailEdit, ...evaluatorRest } = evaluator
              return evaluatorRest
            }),
          },
        }

        // Actualizar el draft en el backend
        test360UpdateDraft(stepOneData.processId, bodyToSend)
          .then((updateResponse) => {
            // Manejar respuesta del endpoint
            if (!updateResponse.success) {
              getError(updateResponse.message)
            }
          })
          .catch((error) => {
            console.error('Error updating test360 draft:', error)
            getError('Error al actualizar el borrador del Test 360')
          })

        getSuccess(t('global.notifications.updateUserSuccess'))
      }

      if (response.status === 'error' && response.message === 'Email or document already exists') {
        getWarning(t('global.notifications.updateUserError'))
      }

      setTableLoading(false)
    } catch (error) {
      setTableLoading(false)
      getError(t('global.notifications.updateUserErrorUnexpected'))
      console.error('error updating evaluator', error)
    }
  }

  interface LangProps {
    language: string
    onChange: (event: SelectChangeEvent) => void
  }

  const handleLanguageChange = (userId: number | undefined, newLanguage: string) => {
    if (!userId) return

    updateLanguage(processId, userId, newLanguage)

    let newUsers: UserAdded[] = []
    let shouldUpdate360 = false
    let test360Body: any = null

    setAddedUsers((prev) => {
      const userIndex = prev.findIndex((user) => user.id === userId)
      if (userIndex !== -1) {
        newUsers = [...prev]
        const updatedUser = { ...newUsers[userIndex], language: newLanguage }
        newUsers[userIndex] = updatedUser

        shouldUpdate360 = users360Data.length > 0
        if (shouldUpdate360) {
          test360Body = {
            test360: {
              users: newUsers.map((user: any) => {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const { id, user_id, user_test_id, document, test_types, evaluators, emailEdit, ...userRest } = user
                return {
                  ...userRest,
                  evaluated_role: user.evaluated_role,
                  evaluators: evaluators?.map((evaluator: any) => {
                    const {
                      id: evalId,
                      user_id: evalUserId,
                      user_test_id: evalTestId,
                      document: evalDoc,
                      emailEdit: evalEmailEdit,
                      ...evaluatorRest
                    } = evaluator
                    return evaluatorRest
                  }),
                }
              }),
            },
          }
        }

        return newUsers
      }
      return prev
    })

    setUserLanguages((prevLanguages) => ({
      ...prevLanguages,
      [userId]: newLanguage,
    }))

    setTimeout(() => {
      if (newUsers.length > 0) {
        dispatch(setUserListAdded(newUsers))

        if (shouldUpdate360 && test360Body) {
          dispatch(setEvaluatedUsers(newUsers))
          test360UpdateDraft(stepOneData.processId, test360Body)
            .then((updateResponse) => {
              // Manejar respuesta del endpoint
              if (!updateResponse.success) {
                getError(updateResponse.message)
              }
            })
            .catch((error) => {
              console.error('Error updating test360 draft:', error)
              getError('Error al actualizar el borrador del Test 360')
            })
        }
      }
    }, 0)
  }

  const handleLanguageChangeAll = (newLanguage: string) => {
    setAddedUsers((prevUsers) => {
      const newUsers = prevUsers.map((user) => ({
        ...user,
        language: newLanguage,
      }))

      dispatch(setUserListAdded(newUsers))

      if (users360Data.length > 0) {
        dispatch(setEvaluatedUsers(newUsers))
        const body = {
          test360: {
            users: newUsers.map((user: any) => {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              const { id, user_id, user_test_id, document, test_types, emailEdit, ...userRest } = user
              return {
                ...userRest,
                evaluated_role: user.evaluated_role,
              }
            }),
          },
        }
        test360UpdateDraft(stepOneData.processId, body)
          .then((updateResponse) => {
            // Manejar respuesta del endpoint
            if (!updateResponse.success) {
              getError(updateResponse.message)
            }
          })
          .catch((error) => {
            console.error('Error updating test360 draft:', error)
            getError('Error al actualizar el borrador del Test 360')
          })
      }

      newUsers.forEach((user) => {
        updateLanguage(processId, user.id, newLanguage)
      })

      return newUsers
    })

    setUserLanguages((prevLanguages) => {
      const updatedLanguages = { ...prevLanguages }

      addedUsers.forEach((user) => {
        updatedLanguages[user.id] = newLanguage
      })
      return updatedLanguages
    })
  }

  const disabledSelectMessage = () => {
    if (!stepFiveData?.testTypes?.includes('Betesa')) {
      getWarning('El cambio de idioma no está disponible para esta prueba')
      return
    }
  }

  const LanguageSwitch: FC<LangProps> = ({ language, onChange }) => {
    return (
      <div onClick={disabledSelectMessage}>
        <Select
          onChange={onChange}
          value={language}
          disabled={!stepFiveData?.testTypes?.includes('Betesa')}
          size="small"
          defaultValue="es"
          sx={{ borderRadius: '8px', width: '110px' }}
        >
          <MenuItem value="es">Español</MenuItem>
          <MenuItem value="en">Inglés</MenuItem>
        </Select>
      </div>
    )
  }

  const handleDisableSendButton = () => {
    if (users360Data.length) {
      if (!test360Date) {
        return true
      }
      const today = dayjs().startOf('day')

      const selectedDate = dayjs(test360Date, ['DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD']).startOf('day')

      return !selectedDate.isValid() || selectedDate.isBefore(today)
    }
    return addedUsers.length < 1
  }

  const handleChangeTest360Date = async (date: string) => {
    setTest360Date(date)

    const parsedDate = dayjs(date, ['DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD'])

    if (!parsedDate.isValid()) {
      return
    }

    const body = {
      test360: {
        finalDate: date,
      },
    }
    dispatch(setFinalDate(date))
    const updateResponse = await updateProcessFinalDate(stepOneData.processId, body)

    // Manejar error 402 de créditos insuficientes
    if (updateResponse && updateResponse.success === false && updateResponse.message) {
      getError(updateResponse.message)
    }
  }

  // MARK: CLEANUP FUNCTION

  usePreventNavigation({
    shouldPrevent: false,
    onNavigationAttempt: handleCleanup,
  })

  const dataSource = results && results.length > 0 ? results : paginatedAddedUsers

  const arrayAddedUsers = dataSource.map((user) => {
    const currentLanguage = userLanguages[user.id] || user.language || 'es'

    return {
      ...user,
      first_name: user.first_name,
      last_name: user.last_name,
      document: user.document,
      email: user.email.toLowerCase(),
      language: (
        <LanguageSwitch language={currentLanguage} onChange={(e) => handleLanguageChange(user.id, e.target.value)} />
      ),
      edit: (
        isEditing: boolean,
        onEdit: () => void,
        onSave: (rowIndex: number) => void,
        onCancel: () => void,
        rowIndex: number
      ) => {
        if (isEditing) {
          return (
            <ButtonsContainer>
              <ButtonSave onClick={() => onSave(rowIndex)}>Guardar</ButtonSave>
              <ButtonCancel onClick={onCancel}>Cancelar</ButtonCancel>
            </ButtonsContainer>
          )
        }
        return (
          <ButtonEdit onClick={onEdit}>
            <EditIcon />
          </ButtonEdit>
        )
      },
    }
  })

  const disableMailEdit = () => {
    const isUserEnglish = addedUsers.some((user) => user.language === 'en')
    return isUserEnglish
  }

  const paginatedEvaluators360 = useMemo(() => {
    if (evaluators360.length === 0) return []

    const modifiedEvaluators = evaluators360.map((evaluator: UserAdded) => ({
      ...evaluator,
      first_name: evaluator.first_name,
      last_name: evaluator.last_name,
      document: evaluator.document,
      email: evaluator.email.toLowerCase(),
      edit: (
        isEditing: boolean,
        onEdit: () => void,
        onSave: (rowIndex: number) => void,
        onCancel: () => void,
        rowIndex: number
      ) => {
        if (isEditing) {
          return (
            <ButtonsContainer>
              <ButtonSave onClick={() => onSave(rowIndex)}>Guardar</ButtonSave>
              <ButtonCancel onClick={onCancel}>Cancelar</ButtonCancel>
            </ButtonsContainer>
          )
        }
        return (
          <ButtonEdit onClick={onEdit}>
            <EditIcon />
          </ButtonEdit>
        )
      },
    }))

    const firstIndex = (pageEvaluators - 1) * DEFAULT_LIMIT
    const lastIndex = firstIndex + DEFAULT_LIMIT

    return modifiedEvaluators.slice(firstIndex, lastIndex)
  }, [evaluators360, pageEvaluators])

  useEffect(() => {
    if (paginatedEvaluators360.length === 0 && pageEvaluators > 1 && evaluators360.length > 0) {
      setPageEvaluators(pageEvaluators - 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageEvaluators, paginatedEvaluators360, evaluators360.length])

  return (
    <MainLayout>
      <Container>
        {users360Data.length ? (
          <HeaderContainer>
            <TitleIcon src={threeSixtyIcon} alt="threeSixtyIcon" />
            <H2 margin="0px 0px 0px 0px">Resumen</H2>
          </HeaderContainer>
        ) : (
          <H2 margin="0px 0px 20px 0px">Resumen</H2>
        )}
        <SectionOne>
          <SectionOneItem>
            <SectionOneItemNumber>{getCountQuestionnaires(stepThreeData)}</SectionOneItemNumber>
            <SectionOneItemDescription>Pruebas a utilizar por usuario</SectionOneItemDescription>
          </SectionOneItem>
          {uploadType || resultsSucess.length ? (
            <>
              <HorizontalDivider />
              <SectionOneItem>
                <SectionOneItemNumber>{resultsSucess.length}</SectionOneItemNumber>
                <SectionOneItemDescription>Usuarios creados exitosamente</SectionOneItemDescription>
              </SectionOneItem>
            </>
          ) : null}
          <HorizontalDivider />
          <SectionOneItem style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <SectionOneItemNumber>{inventoryUniqName || inventoryData?.subscriptionName}</SectionOneItemNumber>
            <SectionOneItemDescription style={{ alignSelf: 'center' }}>Inventario utilizado</SectionOneItemDescription>
          </SectionOneItem>
          <HorizontalDivider />
          <SectionOneItem>
            <SectionOneItemNumber>
              {inventoryData?.subscriptionId === 3 || inventoryData?.subscriptionId === 4
                ? 'Ilimitado'
                : amountInventory}
            </SectionOneItemNumber>
            <SectionOneItemDescription>Cantidad de pruebas restantes de tu inventario</SectionOneItemDescription>
          </SectionOneItem>
          {resultsFail.length > 0 || (uploadType && !tryFlag && resultsFail.length) ? (
            <>
              <HorizontalDivider />
              <SectionOneItem
                onClick={() => {
                  navigate(platformAdminRoutes.NotUpload, { state: { data: usersFailed } })
                }}
              >
                <SectionOneItemNumber style={{ color: '#E44646' }}>{resultsFail.length}</SectionOneItemNumber>
                <SectionOneItemDescription style={{ color: '#E44646' }}>Usuarios no creados</SectionOneItemDescription>
              </SectionOneItem>
            </>
          ) : null}
        </SectionOne>
        <h5 style={{ margin: 5 }}>Lista de usuarios agregados</h5>
        <Row gap="20px" justifyContent="center" alignItems="center">
          <P style={{ width: '200px' }}>Buscar por:</P>
          {!users360Data.length && (
            <Input
              name="identification"
              type="text"
              label="Número de identificación"
              value={identification}
              onChange={(e) => setIdentification(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearchOnEnter(true)
                }
              }}
            />
          )}
          <Input
            name="email"
            type="text"
            label="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearchOnEnter(true)
              }
            }}
          />
          <Input
            name="name"
            type="text"
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearchOnEnter(true)
              }
            }}
          />
          <HighlightOffIcon
            style={{ cursor: 'pointer', color: 'gray' }}
            onClick={() => {
              clearSearch()
              setName('')
              setEmail('')
              setIdentification('')
            }}
          />
          <SearchButton
            onClick={() =>
              searchUsers({
                identification,
                name,
                email,
              })
            }
            style={{ fontSize: 14 }}
          >
            <MagnifyingGlass src={IconMagnifying} />
          </SearchButton>
        </Row>
        <div ref={users360TableRef}>
          <BluesiteTableComponent
            editable={true}
            ActionComponent={ActionDeleteUser}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            data={arrayAddedUsers}
            headers={users360Data.length ? HEADERS_360_USERS_EDITABLE : HEADERS_ADDED_USERS_EDITABLE}
            searchBar={false}
            searchLabel=""
            handlePagination={results.length ? handlePaginationResults : handlePagination}
            totalPages={results.length ? Math.ceil(results.length / 10) : totalPages}
            currentPage={results.length ? pageResults : pageUsersAdded}
            onSave={handleSave}
            loading={tableLoading}
            showNumbers={true}
            languageHeaderOption={handleLanguageChangeAll}
            disableLanguage={!stepFiveData?.testTypes?.includes('Betesa')}
          />
        </div>
        {evaluators360Data.length ? (
          <>
            <Row justifyContent="flex-start" alignItems="center" gap="20px">
              <Test360Icon src={threeSixtyIcon} />
              <H5 style={{ margin: 5 }}>Lista de evaluadores 360°</H5>
            </Row>
            <H5 style={{ fontWeight: 400 }}>
              Verifica la información de los evaluadores que participarán en la evaluación 360°.
            </H5>
            <BluesiteTableComponent
              editable={true}
              checks={false}
              data={paginatedEvaluators360}
              headers={HEADERS_EVALUATORS_EDITABLE}
              searchBar={false}
              searchLabel=""
              currentPage={pageEvaluators}
              totalPages={Math.ceil(evaluators360.length / DEFAULT_LIMIT)}
              handlePagination={setPageEvaluators}
              showNumbers={true}
              onSave={handleSaveEvaluator}
            />
            <Row justifyContent="flex-start" alignItems="center" gap="30px">
              <H5 style={{ margin: 5 }}>Asigna una fecha límite de respuesta</H5>
              <DateWrapper>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="es"
                  dateFormats={{ keyboardDate: 'DD/MM/YYYY' }}
                >
                  <MuiDatePicker
                    label="Seleccione la fecha de cierre"
                    minDate={dayjs()}
                    value={test360Date ? dayjs(test360Date, ['DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD']) : null}
                    onChange={(e) => {
                      const currentDate = new Date()
                      const today = dayjs(currentDate).startOf('day')
                      const selectedDate = dayjs(e).startOf('day')

                      if (selectedDate.isBefore(today)) {
                        getWarning('No se puede seleccionar una fecha anterior a la actual')
                        return
                      }
                      const formattedDate = dayjs(e).format('DD-MM-YYYY')
                      if (!dayjs(formattedDate, 'DD-MM-YYYY', true).isValid()) {
                        return
                      } else {
                        handleChangeTest360Date(formattedDate)
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        sx={{
                          width: '300px',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'transparent',
                              boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
                              borderRadius: '10px',
                            },
                            '&:hover fieldset': {
                              borderColor: 'transparent',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'blue',
                            },
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#2D749C',
                          },
                        }}
                        {...params}
                      />
                    )}
                  />
                </LocalizationProvider>
                <TooltipComponent
                  tooltipText="La evaluación 360° se deshabilitará para quienes no la hayan respondido antes de la fecha que selecciones. Podrás editar esta fecha desde el dashboard de administrador."
                  placement="top"
                  iconColor="#2D749C"
                  backgroundColor="#2D749C"
                />
              </DateWrapper>
            </Row>
          </>
        ) : null}
        {!disableMailEdit() && !users360Data.length ? (
          <>
            <Row gap="20px" alignItems="center" justifyContent="space-between">
              <h5 style={{ margin: 5 }}>Correo electrónico enviado a usuarios</h5>
              <StrokedButton
                onClick={() => {
                  navigate('/create-process/update-email')
                }}
              >
                Editar correo
              </StrokedButton>
            </Row>
            <P>Edita el correo electrónico enviado a los participantes de este proceso, este paso es opcional</P>
            {templateEmailStepFive?.textOne && (
              <TextAreaDiv>
                <p>Hola, Nombre de Usuario. Nos alegra saludarte.</p>
                <p>{templateEmailStepFive?.textOne}</p>
                <p>
                  Para ingresar a la plataforma Bluesite y diligenciar los cuestionarios asignados,{' '}
                  {templateEmailStepFive?.textTwo}
                </p>
                <ul>
                  <li>Ingresa a este enlace</li>
                  <li>Sigue las instrucciones para crear una contraseña y activar tu cuenta.</li>
                  <li>Encontrarás los cuestionarios por diligenciar. Haz clic para iniciar</li>
                </ul>

                <p>
                  La fecha límite para que actives tu cuenta y llenes los cuestionarios es el{' '}
                  {templateEmailStepFive?.date}.
                </p>

                <p>{templateEmailStepFive?.textThree}</p>
                <ul>
                  {templateEmailStepFive?.textThreeOptions?.map((option: string, index: number) => (
                    <li key={index}>{option}</li>
                  ))}
                </ul>

                <p>
                  En caso de que llenes parcialmente el cuestionario, o tengas problemas de conectividad, lo puedes
                  retomar más adelante. Para hacerlo, completa los siguientes pasos:
                </p>
                <ul>
                  <li>
                    Ingresa <u>aquí</u>.
                  </li>
                  <li>En el campo de usuario escribe el correo electrónico al que te llegó este mensaje.</li>
                  <li>Digita la contraseña que creaste al momento de crear tu prueba.</li>
                  <li>Reinicia el cuestionario pendiente.</li>
                </ul>

                <p>{templateEmailStepFive?.textFour}</p>

                <p>
                  Puedes ingresar a Bluesite desde cualquier dispositivo. Para una mejor experiencia, por favor utiliza
                  las versiones más recientes de los siguientes navegadores: Chrome, Firefox, Safari.
                </p>
              </TextAreaDiv>
            )}{' '}
          </>
        ) : null}

        <Row gap="20px" alignItems="center" justifyContent="space-between">
          <BackButton onClick={() => navigate(platformAdminRoutes.CreateUsers)} />
          <Button disabled={handleDisableSendButton()} onClick={() => handleSendToUsers()}>
            Enviar pruebas a los participantes
          </Button>
        </Row>
      </Container>
      <ModalLoader isLoading={loading || showModal || loadingSendExamToUser} />
    </MainLayout>
  )
}

export default UsersCreateProcess
